import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import { cn } from "@/lib/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import ScrollContainer from "react-indiana-drag-scroll";
import { bucketUrlPrefix, defaultRegion } from "common/config";
import { useAuth, useI18N, useVenueDetailsBySlug } from "common/hooks";
import { Storage } from "common/storage";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import React, { useEffect, useState, useRef } from "react";
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuLabel,
//   DropdownMenuRadioGroup,
//   DropdownMenuRadioItem,
//   DropdownMenuSeparator,
//   DropdownMenuTrigger,
// } from "@/shadcn/components/ui/dropdown-menu";
import NoSearchResults from "@/assets/images/notfound.svg";
import "react-medium-image-zoom/dist/styles.css";
import Loader from "@/components/common/Loader";
import { Helmet } from "react-helmet";
import FlagVenuePhoto from "@/features/flag-report/FlagVenuePhoto";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";
import { 
  ArrowLeft,
  ArrowLeftToLine,
  BookOpenText, 
  ChevronUp,
  ChevronDown,
  DoorOpen, 
  Home, 
  LandmarkIcon,
  Menu,
  UtensilsCrossed, 
  Warehouse 
} from "lucide-react";
// import { ArrowsDownUp } from "phosphor-react";
import AnalyticsService from "@/services/analytics";
import FullImageViewer from "@/components/FullImageViewer";

type VenuePhotosOptions = {
  reviewId?: string;
  sort: "latest" | "earliest" | "category";
  area?: string;
};

// We'll extract display sections from the API data

// Define types for hybrid venue structure
interface TagOption {
  optionTitle: string;
  optionTitleId?: number;
  category_display_section_id?: string;
}

interface QuestionOption {
  tagOptions?: TagOption[];
  category_display_section_id?: string;
  category_display_section_name?: string;
  title?: string;
  titleId?: number;
}

interface DisplaySection {
  id: number;
  category_id: number;
  name: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface LevelOption {
  levelPhotos?: Array<{
    levelName?: string;
    sort_order?: number;
    width?: number;
    height?: number;
    [key: string]: any;
  }>;
  questionOptions?: QuestionOption[];
  questionDisplaySections?: DisplaySection[];
  name?: string;
  levelName?: string;
  level?: string;
}

// Define types for the photo data structure
interface PhotoItem {
  path: string;
  alt_text?: string;
  category_name: string;
  category_id: number;
  tag_name: string;
  created_at: string;
  photo_type?: string;
  review_photos_id?: string;
  width?: number;
  height?: number;
  sort_order?: number;
  // Venue type flag to determine rendering behavior
  venueType?: 'Basic' | 'Hybrid';
  // Accessibility tag properties
  qtags?: Array<{
    tagId: number;
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  tags?: Array<{
    tagName: string;
    categoryDisplaySectionId?: string;
  }>;
  // Display sections for accessibility tags
  displaySections?: Record<string, string>;
}

interface CategoryGroup {
  [subcategory: string]: PhotoItem[];
}

interface PhotoCategories {
  [category: string]: CategoryGroup;
}

export const Route = createFileRoute("/venuephotos/$venueSlug")({
  component: VenuePhotos,
  validateSearch: (search: Record<string, unknown>): VenuePhotosOptions => {
    // validate and parse the search params into a typed state
    return {
      sort: (search.sort as "latest" | "earliest" | "category") || "category",
      reviewId: search.reviewId as string | undefined,
      area: search.area as string | undefined,
    };
  },
});

// Function to sort photos based on the selected sort option
const sortPhotos = (photos: PhotoItem[], sortOption: string): PhotoItem[] => {
  // Create a copy of the photos array to avoid mutating the original
  const sortedPhotos = [...photos];
  
  switch (sortOption) {
    case "latest":
      // Sort by created_at date in descending order (newest first)
      return sortedPhotos.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    
    case "earliest":
      // Sort by created_at date in ascending order (oldest first)
      return sortedPhotos.sort((a, b) => {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
    
    case "category":
      // For category sorting, sort by category name, then subcategory name, then sort_order
      return sortedPhotos.sort((a, b) => {
        // First compare by category name
        const categoryComparison = a.category_id - b.category_id;
        if (categoryComparison !== 0) return categoryComparison;
        
        // If categories are the same, compare by subcategory/tag
        const subcategoryComparison = a.sort_order - b.sort_order;
        if (subcategoryComparison !== 0) return subcategoryComparison;
        
        // If both category and subcategory are the same, use sort_order
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
    
    default:
      // Default to the original API order using sort_order field only
      return sortedPhotos.sort((a, b) => {
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
  }
};

function VenuePhotos() {
  const { venueSlug: routeSlug } = Route.useParams();
  const { sort, area } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const i18n = useI18N();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom<any>(actionToPerformValueAfterLoginAtom);

  const [fullImageView, setFullImageView] = useState(false);
  const [currentImage, setCurrentImage] = useState<PhotoItem | null>(null);
  const [isFlagVenuePhotoOpen, setIsFlagVenuePhotoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<PhotoItem | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>(area || "All");
  const [activeSubcategory, setActiveSubcategory] = useState<string>("");
  // Create a ref object to store subcategory refs
  const subcategoryRefs = useRef<Record<string, Record<string, HTMLDivElement>>>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [venueSlug, setVenueSlug] = useState(routeSlug);

  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const { location } = useGeoLocation();
  
  // Use the venue details hook to fetch all venue data including photos
  const {
    data,
    error,
    isLoading,
    refetch: loadVenueDetails
  } = useVenueDetailsBySlug({
    slug: routeSlug,
    latitude: location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude: location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: true
  });
  
  // Define function to reload photos (used in useEffect and other places)
  const loadPhotos = React.useCallback(() => {
    loadVenueDetails();
  }, [loadVenueDetails]);

  const [reviewPhotos, setReviewPhotos] = useState<PhotoItem[]>([]);
  const [photosByCategory, setPhotosByCategory] = useState<PhotoCategories>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [totalSubcategories, setTotalSubcategories] = useState(0);

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "flagPhoto") {
        setSelectedImage(actionToPerformValueAfterLogin);
        setIsFlagVenuePhotoOpen(true);
      }

      setActionToPerformAfterLogin("");
      setActionToPerformValueAfterLogin("");
    }
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("errorLoadingPhotos");
      AnalyticsService.trackMx("errorLoadingPhotos", {});
      console.error(error);
    }
  }, [error]);

  useEffect(() => {
    // When sort option changes, re-sort the existing photos
    /*if (reviewPhotos.length > 0) {
      const sortedPhotos = sortPhotos(reviewPhotos, sort);
      setReviewPhotos(sortedPhotos);
      
      // Also update the grouped photos by category
      const photoGroups: PhotoCategories = {};
      const uniqueCategories: Set<string> = new Set();
      let subcategoryCount = 0;
  
      sortedPhotos.forEach((photo) => {
        const categoryName = photo.category_name || "Uncategorized";
        const subcategoryName = photo.tag_name || "Uncategorized";
        uniqueCategories.add(categoryName);
  
        if (!photoGroups[categoryName]) {
          photoGroups[categoryName] = {};
        }
        if (!photoGroups[categoryName][subcategoryName]) {
          photoGroups[categoryName][subcategoryName] = [];
          subcategoryCount++;
        }
        photoGroups[categoryName][subcategoryName].push(photo);
      });
  
      setPhotosByCategory(photoGroups);
      setCategories(Array.from(uniqueCategories));
      setTotalSubcategories(subcategoryCount);
    } else {
      // If no photos are loaded yet, load them from the API
      loadPhotos();
    }
    */
    if (reviewPhotos.length === 0) {
      loadPhotos();
    }
  }, [loadPhotos, reviewPhotos.length]);

  // Store category images for use in the sidebar
  const [categoryImages, setCategoryImages] = useState<Record<string, { light: string, dark: string }>>({});

  // Organize photos by category and subcategory
  useEffect(() => {
    if (!data) return;
  
    const enrichAndGroupPhotos = async () => {
      AnalyticsService.trackGAEventHandler("scrolledVenuePhotosInList");
      AnalyticsService.trackMx("scrolledVenuePhotosInList", {
        VenueId: data?.details?.id,
        VenueBrandId: data?.details?.brandId,
        VenueBrandName: data?.details?.brandName,
        VenueAddress: data?.details?.address,
        VenueName: data?.details?.name,
        VenueTypes: data?.details?.types,
      });
  
      // Indoor, Outdoor and VSH categories IDs
      const vshIndoorCatId = 96;
      const vshOutdoorCatId = 216;
      const regularIndoorCatId = 58;
      const regularOutdoorCatId = 172;
      
      // Extract photos from review_tags
      const allPhotos = [];
      const catImages: Record<string, { light: string, dark: string }> = {};
      const displaySections: Record<string, string> = {};
      
      // Create a set to collect all category names, even those without photos
      const allCategoryNames = new Set<string>();
      // Create a map to track category IDs by name
      const categoryIdMap = new Map<string, number>();
      // Track subcategories by category ID for special VSH handling
      // const subcategoriesByCatId = new Map<number, string[]>();
      
      // Loop through all categories for Basic and Hybris venues
      if (
        data?.details?.templateType !== "Basic" &&
        data?.details?.isPublicTemplate === "Yes" &&
        Array.isArray(data.accesibilityCategoriesHybrid)
      ) {
        // Hybrid venue
        // Step 1: Collect known sub-category names from hybrid indoor/outdoor categories
        const hybridKnownSubCategoryNames = new Set();

        data.accesibilityCategoriesHybrid.forEach(category => {
          if ([regularIndoorCatId, regularOutdoorCatId].includes(category.catId)) {
            category.categoryOptions?.forEach(categoryOption => {
              categoryOption.pageChildrenCategories?.forEach(pageCategory => {
                pageCategory.accessibilityOptions?.forEach(accessibilityOption => {
                  accessibilityOption.levelOptions?.forEach(level => {
                    if (level.level) {
                      hybridKnownSubCategoryNames.add(level.level.trim());
                    }
                  });
                });
              });
            });
          }
        });

        // Step 2: Build VSH tag maps for hybrid structure
        const hybridVSHIndoorQTagsBySubCat = new Map();
        const hybridVSHOutdoorQTagsBySubCat = new Map();

        data.accesibilityCategoriesHybrid.forEach(category => {
          if ([vshIndoorCatId, vshOutdoorCatId].includes(category.catId)) {
            const mapToUse = category.catId === vshIndoorCatId ? hybridVSHIndoorQTagsBySubCat : hybridVSHOutdoorQTagsBySubCat;

            category.categoryOptions?.forEach(categoryOption => {
              categoryOption.pageChildrenCategories?.forEach(pageCategory => {
                pageCategory.accessibilityOptions?.forEach(accessibilityOption => {
                  accessibilityOption.levelOptions?.forEach(level => {
                    const subCatName = level.level?.trim();
                    if (!subCatName || !hybridKnownSubCategoryNames.has(subCatName)) return;

                    const vshQtags: any[] = [];

                    level.questionOptions?.forEach(question => {
                      const tagOptions = Array.isArray(question.tagOptions) ? question.tagOptions : [];
                      tagOptions.forEach(tag => {
                        // Add type assertion to fix TypeScript errors
                        const typedTag = tag as TagOption;
                        if (typedTag.optionTitle && typedTag.optionTitle !== subCatName) {
                          // Add type assertion for question object
                          const typedQuestion = question as unknown as QuestionOption;
                          vshQtags.push({
                            tagId: typedTag.optionTitleId || 0,
                            tagName: typedTag.optionTitle,
                            categoryDisplaySectionId: typedQuestion.category_display_section_id || ''
                          });
                        }
                      });
                    });

                    const existing = mapToUse.get(subCatName) || [];
                    mapToUse.set(subCatName, existing.concat(vshQtags));
                  });
                });
              });
            });
          }
        });

        // Step 3: Process Hybrid venue photos with merged VSH tags
        data.accesibilityCategoriesHybrid.forEach((category) => {
          if (category.catName) {
            // Add to all category names set
            allCategoryNames.add(category.catName);
            // Store category ID by name
            if (category.catId) {
              categoryIdMap.set(category.catName, category.catId);
            }
            
            catImages[category.catName] = {
              light: category.catImage || "",
              dark: category.catImageDark || "",
            };
          }
      
          category.categoryOptions?.forEach((categoryOption) => {
            categoryOption.pageChildrenCategories?.forEach((pageCategory) => {
              pageCategory.accessibilityOptions?.forEach((accessibilityOption) => {
                accessibilityOption.levelOptions?.forEach((level) => {
                  // Type-safe handling of level
                  const safeLevel = level as unknown as LevelOption;
                  // Extract display sections if available
                  if (Array.isArray(safeLevel.questionDisplaySections)) {
                    // Add display sections to our map
                    safeLevel.questionDisplaySections.forEach(section => {
                      if (section.id && section.name) {
                        displaySections[section.id.toString()] = section.name;
                      }
                    });
                  }
            
                  // Process tags for hybrid venues
                  const subCatName = safeLevel.level?.trim() || '';
                  let qtags = [];
                  
                  // Extract tags from questionOptions -> tagOptions
                  safeLevel.questionOptions?.forEach((question: QuestionOption) => {
                    // Safe access to tagOptions with type checking
                    const tagOptions = Array.isArray(question.tagOptions) ? question.tagOptions : [];
                    
                    tagOptions.forEach((tag: TagOption) => {
                      // Handle different property structures in hybrid venues using type assertion
                      const tagName = tag.optionTitle || '';
                      if (tagName) {
                        qtags.push({
                          tagId: tag.optionTitleId || 0,
                          tagName: tagName,
                          categoryDisplaySectionId: question.category_display_section_id || ''
                        });
                      }
                    });
                  });

                  // Merge in VSH qtags if sub-category matches
                  if (category.catId === regularIndoorCatId && hybridVSHIndoorQTagsBySubCat.has(subCatName)) {
                    qtags = qtags.concat(hybridVSHIndoorQTagsBySubCat.get(subCatName));
                  } else if (category.catId === regularOutdoorCatId && hybridVSHOutdoorQTagsBySubCat.has(subCatName)) {
                    qtags = qtags.concat(hybridVSHOutdoorQTagsBySubCat.get(subCatName));
                  }
                  
                  // Create tags array from level name as fallback
                  const tags = [{
                    tagName: (safeLevel as any).name || (safeLevel as any).levelName || 'Uncategorized',
                    categoryDisplaySectionId: ''
                  }];
                  
                  safeLevel.levelPhotos?.forEach((photo) => {
                    allPhotos.push({
                      ...photo,
                      category_name: category.catName || "Uncategorized",
                      tag_name: photo.levelName,
                      category_id: category.catId || 0,
                      sort_order: photo.sort_order || 0,
                      width: photo.width || 800,
                      height: photo.height || 600,
                      venueType: 'Hybrid',
                      qtags: qtags,
                      tags: tags,
                      displaySections: displaySections
                    });
                  });
                });
              });
            });
          });
        });
      } else {
        // Basic venue
        // Step 1: Collect all known sub-category names from regular categories
        const knownSubCategoryNames = new Set();  
        data.accesibilityCategories.forEach(category => {
          if ([regularIndoorCatId, regularOutdoorCatId].includes(category.catId)) {
            category.tags?.forEach(tagGroup => {
              tagGroup.reviewPhotos?.forEach(photo => {
                if (photo.tag_name) {
                  knownSubCategoryNames.add(photo.tag_name.trim());
                }
              });
            });
          }
        });

        // Step 2: Build VSH sub-category → tags map
        const vshIndoorQTagsBySubCat = new Map();
        const vshOutdoorQTagsBySubCat = new Map();

        data?.accesibilityCategories?.forEach(category => {
          if ([vshIndoorCatId, vshOutdoorCatId].includes(category.catId)) {
            category.tags?.forEach(tagGroup => {
              const qtags = tagGroup.qtags || [];
              const mapToUse = category.catId === vshIndoorCatId ? vshIndoorQTagsBySubCat : vshOutdoorQTagsBySubCat;

              qtags.forEach(currentTag => {
                const subCatName = currentTag.tagName?.trim();
                if (!subCatName || !knownSubCategoryNames.has(subCatName)) return;

                const otherTags = qtags.filter(t => t.tagName !== subCatName);
                const existing = mapToUse.get(subCatName) || [];
                mapToUse.set(subCatName, existing.concat(otherTags));
              });
            });
          }
        });

        // Step 3: Process all categories and assign photos
        if (data?.accesibilityCategories && Array.isArray(data.accesibilityCategories)) {
          data.accesibilityCategories.forEach(category => {
            if (category.catName) {
              // Add to all category names set
              allCategoryNames.add(category.catName);
              // Store category ID by name
              if (category.catId) {
                categoryIdMap.set(category.catName, category.catId);
              }
              
              catImages[category.catName] = {
                light: category.catImage || '',
                dark: category.catImageDark || ''
              };
            }
            
            // Extract display sections if available
            if (category.cat_display_sections && typeof category.cat_display_sections === 'object') {
              // Add display sections to our map
              Object.entries(category.cat_display_sections).forEach(([key, value]) => {
                if (typeof value === 'string') {
                  displaySections[key] = value;
                }
              });
            }
            
            if (category.tags && Array.isArray(category.tags)) {
              category.tags.forEach(tagGroup => {
                if (tagGroup.reviewPhotos && Array.isArray(tagGroup.reviewPhotos)) {
                  tagGroup.reviewPhotos.forEach(photo => {
                    const subCatName = photo.tag_name?.trim() || '';
                    // Extract qtags from the tagGroup if available
                    // For basic venues, we'll use the tagGroup's qtags directly if available
                    let qtags = tagGroup.qtags && Array.isArray(tagGroup.qtags) ? 
                      tagGroup.qtags.map(qtag => ({
                        tagId: qtag.tagId || 0,
                        tagName: qtag.tagName || '',
                        categoryDisplaySectionId: qtag.categoryDisplaySectionId || ''
                      })) : [];
                    
                    // Merge in VSH tags if sub-category matches
                    if (category.catId === regularIndoorCatId && vshIndoorQTagsBySubCat.has(subCatName)) {
                      qtags = qtags.concat(vshIndoorQTagsBySubCat.get(subCatName));
                    } else if (category.catId === regularOutdoorCatId && vshOutdoorQTagsBySubCat.has(subCatName)) {
                      qtags = qtags.concat(vshOutdoorQTagsBySubCat.get(subCatName));
                    }
                    
                    // We'll create tags from the tag_name as a fallback
                    const tags = [{
                      tagName: photo.tag_name || 'Uncategorized',
                      categoryDisplaySectionId: ''
                    }];
                    
                    allPhotos.push({
                      ...photo,
                      category_name: category.catName || 'Uncategorized',
                      tag_name: photo.tag_name || 'Uncategorized',
                      category_id: category.catId || 0,
                      sort_order: tagGroup.sort_order || 0,
                      width: photo.width || 800,
                      height: photo.height || 600,
                      venueType: 'Basic',
                      qtags: qtags,
                      tags: tags,
                      displaySections: displaySections
                    });
                  });
                }
              });
            }
          });
        }
      }
      
      // Set the category images for use in the sidebar
      setCategoryImages(catImages);

      // Sort the photos based on the selected sort option
      const sortedPhotos = sortPhotos(allPhotos, sort);
  
      setReviewPhotos(sortedPhotos);
      setTotalPhotos(sortedPhotos.length);
  
      // Step 2: Group photos by category and subcategory
      const photoGroups: PhotoCategories = {};
      const uniqueCategories: Set<string> = new Set();
      let subcategoryCount = 0;
      
      // First, initialize photoGroups with all categories, even those without photos
      allCategoryNames.forEach(categoryName => {
        uniqueCategories.add(categoryName);
        photoGroups[categoryName] = {};
      });

      // Then add photos to their categories and subcategories
      sortedPhotos.forEach((photo) => {
        const categoryName = photo.category_name || "Uncategorized";
        const subcategoryName = photo.tag_name || "Uncategorized";
        uniqueCategories.add(categoryName);
  
        if (!photoGroups[categoryName]) {
          photoGroups[categoryName] = {};
        }
        if (!photoGroups[categoryName][subcategoryName]) {
          photoGroups[categoryName][subcategoryName] = [];
          subcategoryCount++;
        }
        photoGroups[categoryName][subcategoryName].push(photo);
      });
  
      // Special handling for VSH categories - inherit subcategories from regular categories
      // Find category names corresponding to the IDs
      let vshIndoorCategoryName = "";
      let vshOutdoorCategoryName = "";
      let regularIndoorCategoryName = "";
      let regularOutdoorCategoryName = "";
      
      // Map category names to their IDs
      categoryIdMap.forEach((id, name) => {
        if (id === vshIndoorCatId) vshIndoorCategoryName = name;
        if (id === vshOutdoorCatId) vshOutdoorCategoryName = name;
        if (id === regularIndoorCatId) regularIndoorCategoryName = name;
        if (id === regularOutdoorCatId) regularOutdoorCategoryName = name;
      });
      
      // Copy subcategories from regular Indoor to VSH Indoor
      if (vshIndoorCategoryName && regularIndoorCategoryName && photoGroups[regularIndoorCategoryName]) {
        // Check if regular Indoor has any subcategories
        const regularIndoorSubcategories = Object.keys(photoGroups[regularIndoorCategoryName]);
        
        if (regularIndoorSubcategories.length > 0) {
          // Make sure VSH Indoor category exists
          if (!photoGroups[vshIndoorCategoryName]) {
            photoGroups[vshIndoorCategoryName] = {};
          }
          
          // Copy all subcategories from regular Indoor to VSH Indoor
          regularIndoorSubcategories.forEach(subcategoryName => {
            // Only add subcategories that don't already exist
            if (!photoGroups[vshIndoorCategoryName][subcategoryName]) {
              photoGroups[vshIndoorCategoryName][subcategoryName] = [];
              subcategoryCount++;
            }
          });
        } else {
          // If regular Indoor has no subcategories, remove VSH Indoor category
          if (photoGroups[vshIndoorCategoryName]) {
            delete photoGroups[vshIndoorCategoryName];
            // Remove from allCategoryNames if it exists
            allCategoryNames.delete(vshIndoorCategoryName);
          }
        }
      }
      
      // Copy subcategories from regular Outdoor to VSH Outdoor
      if (vshOutdoorCategoryName && regularOutdoorCategoryName && photoGroups[regularOutdoorCategoryName]) {
        // Check if regular Outdoor has any subcategories
        const regularOutdoorSubcategories = Object.keys(photoGroups[regularOutdoorCategoryName]);
        
        if (regularOutdoorSubcategories.length > 0) {
          // Make sure VSH Outdoor category exists
          if (!photoGroups[vshOutdoorCategoryName]) {
            photoGroups[vshOutdoorCategoryName] = {};
          }
          
          // Copy all subcategories from regular Outdoor to VSH Outdoor
          regularOutdoorSubcategories.forEach(subcategoryName => {
            // Only add subcategories that don't already exist
            if (!photoGroups[vshOutdoorCategoryName][subcategoryName]) {
              photoGroups[vshOutdoorCategoryName][subcategoryName] = [];
              subcategoryCount++;
            }
          });
        } else {
          // If regular Outdoor has no subcategories, remove VSH Outdoor category
          if (photoGroups[vshOutdoorCategoryName]) {
            delete photoGroups[vshOutdoorCategoryName];
            // Remove from allCategoryNames if it exists
            allCategoryNames.delete(vshOutdoorCategoryName);
          }
        }
      }
      
      setPhotosByCategory(photoGroups);
      // Use all category names for the filter tabs, not just those with photos
      setCategories(Array.from(allCategoryNames));
      setTotalSubcategories(subcategoryCount);
  
      if (data.details?.business?.slug) {
        setVenueSlug(data.details.business.slug);
      }
    };
  
    enrichAndGroupPhotos();
  }, [data, sort]);  

  // Update URL when filter changes
  useEffect(() => {
    if (activeFilter !== "All") {
      navigate({
        search: {
          sort: sort,
          area: activeFilter,
        } as any,
        replace: true,
      });
    } else {
      navigate({
        search: {
          sort: sort,
        } as any,
        replace: true,
      });
    }
  }, [activeFilter, sort, navigate]);

  // Set the header height for sidebar positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerElement = document.querySelector('.header-container');
      if (headerElement) {
        const headerHeight = headerElement.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      }
    };

    // Update on load and resize
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    // Update when data loads (which might change header size)
    if (!isLoading && reviewPhotos.length > 0) {
      setTimeout(updateHeaderHeight, 100);
    }

    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [isLoading, reviewPhotos.length]);

  // Focus sidebar when it's opened
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (sidebarOpen && sidebarRef.current) {
      sidebarRef.current.focus();
    }
  }, [sidebarOpen]);

  // const handleListSort = (value: "latest" | "earliest" | "category") => {
  //   console.log("handleListSort value", value);

  //   if (value === "latest") {
  //     AnalyticsService.trackGAEventHandler("sortedPhotosByLatest");
  //     AnalyticsService.trackMx("sortedPhotosByLatest", {
  //       VenueId: data?.details?.id,
  //       VenueBrandId: data?.details?.brandId,
  //       VenueBrandName: data?.details?.brandName,
  //       VenueAddress: data?.details?.address,
  //       VenueName: data?.details?.name,
  //       VenueTypes: data?.details?.types,
  //       ByFilter: "Latest",
  //     });
  //   } else if (value === "earliest") {
  //     AnalyticsService.trackGAEventHandler("sortedPhotosByEarliest");
  //     AnalyticsService.trackMx("sortedPhotosByEarliest", {
  //       VenueId: data?.details?.id,
  //       VenueBrandId: data?.details?.brandId,
  //       VenueBrandName: data?.details?.brandName,
  //       VenueAddress: data?.details?.address,
  //       VenueName: data?.details?.name,
  //       VenueTypes: data?.details?.types,
  //       ByFilter: "Earliest",
  //     });
  //   } else {
  //     AnalyticsService.trackGAEventHandler("sortedPhotosByPopularity");
  //     AnalyticsService.trackMx("sortedPhotosByPopularity", {
  //       VenueId: data?.details?.id,
  //       VenueBrandId: data?.details?.brandId,
  //       VenueBrandName: data?.details?.brandName,
  //       VenueAddress: data?.details?.address,
  //       VenueName: data?.details?.name,
  //       VenueTypes: data?.details?.types,
  //       ByFilter: "Popularity",
  //     });
  //   }

  //   navigate({
  //     search: {
  //       sort: value as "latest" | "earliest" | "category",
  //       ...(activeFilter !== "All" ? { area: activeFilter } : {}),
  //     } as any,
  //     replace: true,
  //   });
  // };

  const handleFlagVenuePhotoClose = () => {
    AnalyticsService.trackGAEventHandler("flagPhotoReportClose");
    AnalyticsService.trackMx("flagPhotoReportClose", {});
    setIsFlagVenuePhotoOpen(false);
  };

  const handleOpenFlagTracking = () => {
    AnalyticsService.trackGAEventHandler("flagPhotoReportOpen");
    AnalyticsService.trackMx("flagPhotoReportOpen", {});
  };

  const handleOpenFullImage = (image) => {
    setCurrentImage(image);
    setFullImageView(true);
    
    // Track opening the full image viewer
    AnalyticsService.trackGAEventHandler("openPhotoLightbox");
    AnalyticsService.trackMx("openPhotoLightbox", {
      VenueId: data?.details?.id,
      VenueBrandId: data?.details?.brandId,
      VenueBrandName: data?.details?.brandName,
      VenueAddress: data?.details?.address,
      VenueName: data?.details?.name,
      VenueTypes: data?.details?.types,
      CategoryName: image.category_name,
      SubcategoryName: image.tag_name,
    });
  };

  const handleFlagPhoto = (image) => {
    handleOpenFlagTracking();

    if (!user || !isLoggedIn) {
      setActionToPerformAfterLogin("flagPhoto");
      setActionToPerformValueAfterLogin(image);
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setSelectedImage(image);
    setIsFlagVenuePhotoOpen(true);
  };

  // Check if dark mode is enabled
  const isDark = document.documentElement.classList.contains('dark');

  // Get icon based on category name
  const getCategoryIcon = (categoryName) => {
    // Check if we have a custom image for this category from the API
    if (categoryImages[categoryName]) {
      const imageUrl = categoryImages[categoryName][isDark ? 'dark' : 'light'];
      
      if (imageUrl) {
        return (
          <img 
            className="w-[20px] h-[20px]" 
            src={bucketUrlPrefix + imageUrl} 
            alt=""
            aria-hidden="true"
          />
        );
      }
    }
    
    // Fallback to default icons if no custom image is available
    const iconMap = {
      "Entrances & exits": <DoorOpen size={20} />,
      "Indoor areas": <Warehouse size={20} />,
      "Outdoor areas": <LandmarkIcon size={20} />,
      "Bathroom": <Home size={20} />,
      "Seating": <UtensilsCrossed size={20} />,
      "Services": <BookOpenText size={20} />
    };
    
    return iconMap[categoryName] || <Home size={20} />;
  };

  // Toggle sidebar open/closed
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle subcategory selection
  const handleSubcategorySelect = (categoryName, subcategoryName) => {
    // Check if we're changing from 'All' to a specific category
    const isChangingFromAll = activeFilter === "All" && categoryName !== "All";
    
    // Highlight the selected subcategory in the sidebar
    setActiveFilter(categoryName);
    setActiveSubcategory(subcategoryName);
    
    // Function to scroll to the subcategory
    const scrollToSubcategory = () => {
      if (subcategoryRefs.current[categoryName]?.[subcategoryName]) {
        // Find the content area element
        const contentArea = document.querySelector('.content-area');
        if (contentArea) {
          // Get the position of the subcategory element relative to the content area
          const subcategoryElement = subcategoryRefs.current[categoryName][subcategoryName];
          
          // Calculate the position to scroll to
          const subcategoryPosition = subcategoryElement.offsetTop;
          
          // Scroll the content area to the subcategory name row
          contentArea.scrollTo({
            top: subcategoryPosition - 250, // Larger offset to ensure the heading is visible
            behavior: 'smooth'
          });
        }
      }
    };
    
    // If changing from 'All' to a specific category, add a small delay
    // to allow the content to update before scrolling
    if (isChangingFromAll) {
      setTimeout(scrollToSubcategory, 50);
    } else {
      // Otherwise scroll immediately
      scrollToSubcategory();
    }
    
    // Close the sidebar on mobile
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
    
    // Add specific tracking for subcategory similar to category chips
    const cleanSubcategoryName = subcategoryName.replace(/[\s,&()/\\:;.'\-–—_]+/g, '');
    const eventName = `viewed${cleanSubcategoryName}Photo`;
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {
      VenueId: data?.details?.id,
      VenueBrandId: data?.details?.brandId,
      VenueBrandName: data?.details?.brandName,
      VenueAddress: data?.details?.address,
      VenueName: data?.details?.name,
      VenueTypes: data?.details?.types,
      CategoryName: categoryName,
      SubcategoryName: subcategoryName,
      FromSidebar: true
    });
  };

  // Function to toggle category expansion
  const toggleCategoryExpansion = (categoryName: string) => {
    if (expandedCategories.includes(categoryName)) {
      setExpandedCategories(expandedCategories.filter(cat => cat !== categoryName));
    } else {
      setExpandedCategories([...expandedCategories, categoryName]);
    }
  };

  // Helper function to scroll content area to top
  const scrollContentToTop = () => {
    const contentArea = document.querySelector('.content-area');
    if (contentArea) {
      contentArea.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  
  const renderFilterTabs = () => {
    return (
      <div className="py-1 w-full">
        <ScrollContainer className="cursor-grab active:cursor-grabbing overflow-x-auto" hideScrollbars={true}>
          <div className="flex flex-row space-x-4 md:space-x-6 pb-2 min-w-max">
            <button
              onClick={() => {
                setActiveFilter("All");
                // When switching to All, keep the current expanded categories
                // Reset scroll position
                scrollContentToTop();
                
                // Track selecting All photos
                AnalyticsService.trackGAEventHandler("viewedAllPhotoChip");
                AnalyticsService.trackMx("viewedAllPhotoChip", {
                  VenueId: data?.details?.id,
                  VenueBrandId: data?.details?.brandId,
                  VenueBrandName: data?.details?.brandName,
                  VenueAddress: data?.details?.address,
                  VenueName: data?.details?.name,
                  VenueTypes: data?.details?.types,
                });
              }}
              className={cn(
                "py-[10px] px-[16px] rounded-[20px] whitespace-nowrap font-medium transition-colors text-sm text-primary-800 dark:text-primary-100",
                activeFilter === "All"
                  ? "bg-secondary-400 dark:text-primary-900"
                  : "bg-primary-100 dark:bg-[#37354D]"
              )}
            >
              All
            </button>
            
            {categories.map((categoryName) => (
              <button
                key={categoryName}
                onClick={() => {
                  setActiveFilter(categoryName);
                  // Ensure the category is expanded when selected
                  if (!expandedCategories.includes(categoryName)) {
                    setExpandedCategories([...expandedCategories, categoryName]);
                  }
                  // Reset scroll position
                  scrollContentToTop();
                  
                  // Track selecting specific category
                  const cleanCategoryName = categoryName.replace(/[\s,&()/\\:;.'\-–—_]+/g, '');
                  const eventName = `viewed${cleanCategoryName}Photo`;
                  AnalyticsService.trackGAEventHandler(eventName);
                  AnalyticsService.trackMx(eventName, {
                    VenueId: data?.details?.id,
                    VenueBrandId: data?.details?.brandId,
                    VenueBrandName: data?.details?.brandName,
                    VenueAddress: data?.details?.address,
                    VenueName: data?.details?.name,
                    VenueTypes: data?.details?.types,
                    CategoryName: categoryName
                  });
                }}
                className={cn(
                  "py-[10px] px-[16px] rounded-[20px] whitespace-nowrap font-medium transition-colors text-sm text-primary-800 dark:text-primary-100",
                  activeFilter === categoryName
                    ? "bg-secondary-400 dark:text-primary-900"
                    : "bg-primary-100 dark:bg-[#37354D]"
                )}
              >
                {categoryName}
              </button>
            ))}
          </div>
        </ScrollContainer>
      </div>
    );
  };

  const renderPhotoGrid = () => {
    // Filter categories based on the active filter
    const categoriesToShow = Object.entries(photosByCategory)
      .filter(([categoryName]) => activeFilter === "All" || activeFilter === categoryName);
    
    return (
      <div className="space-y-4">
        {categoriesToShow.map(([categoryName, subcategories]) => {
          const subcategoryCount = Object.keys(subcategories).length;
          
          // Initialize category refs if not already done
          if (!subcategoryRefs.current[categoryName]) {
            subcategoryRefs.current[categoryName] = {};
          }
          
          return (
            <div key={categoryName} className="mb-12">
              <div className="bg-primary-100 dark:bg-primary-800 py-3 mb-2">
                {/* Title row */}
                <h2 className="text-xl font-semibold text-primary-800 dark:text-primary-100 px-3" tabIndex={0} aria-label={`${categoryName} ${subcategoryCount} areas`}>
                  {categoryName} ({subcategoryCount})
                </h2>
              </div>
              
              <div className="space-y-4">
                {Object.entries(subcategories).map(([subcategoryName, photos]) => (
                  <div 
                    key={subcategoryName} 
                    className="mb-6 scroll-mt-20" 
                    ref={el => {
                      if (el) {
                        subcategoryRefs.current[categoryName][subcategoryName] = el;
                      }
                    }}
                  >
                    <h3 className="text-lg font-medium mb-1 pl-4 text-primary-700 dark:text-primary-200" tabIndex={0}>
                      {subcategoryName}
                    </h3>
                    <p className="text-sm text-primary-500 dark:text-primary-400 mb-3 pl-4" tabIndex={0}>
                      {photos.length > 0 ? `${photos.length} photo${photos.length !== 1 ? 's' : ''}` : "No photos"}
                    </p>
                    {photos.length > 0 && (
                      <RowsPhotoAlbum
                        photos={photos.map((image) => ({
                          src: bucketUrlPrefix + image.path,
                          width: image.width,
                          height: image.height,
                          alt: image.alt_text || "",
                        }))}
                        spacing={8}
                        targetRowHeight={100}
                        // Use a function to determine max photos based on container width
                        rowConstraints={(containerWidth) => {
                          // For mobile screens (< 768px): 2 photos
                          // For iPad/tablet screens (768px - 1024px): 2 photos
                          // For desktop screens (> 1024px): 4 photos
                          let maxPhotos = 2; // Default for mobile and iPad
                          if (containerWidth > 1024) {
                            maxPhotos = 3; // Desktop screens
                          }
                          return {
                            maxPhotos,
                            minPhotos: 1
                          };
                        }}
                        onClick={({ index }) => handleOpenFullImage(photos[index])}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="px-4">
      <Helmet>
        <title>All Photos | Sociability</title>
        <style>{`
          .no-scrollbar {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;     /* Firefox */
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;            /* Chrome, Safari, Opera */
          }

          /* Layout styles */
          html, body {
            overflow: hidden; /* Prevent the parent page from scrolling */
            height: 100%;
          }
          
          .main-layout {
            display: flex;
            width: 100%;
          }
          
          .sidebar-container {
            transition: width 0.3s ease, opacity 0.2s ease;
            position: sticky;
            top: 120px; /* Fixed position from top */
            height: calc(100vh - 120px);
            width: 0;
            opacity: 0;
            overflow-y: auto;
            max-height: calc(100vh - 120px);
            z-index: 20;
            padding-bottom: 120px; /* Add extra padding to ensure scrollbar extends to the bottom */
          }
          
          .sidebar-container.open {
            width: 250px;
            opacity: 1;
          }
          
          .content-area {
            transition: width 0.3s ease;
            width: 100%;
            max-height: calc(100vh - 250px); /* Adjust based on header height */
            overflow-y: auto; /* Make the content area scrollable */
          }
          
          .content-area.sidebar-open {
            width: calc(100% - 250px);
          }
          
          .sidebar-backdrop {
            transition: opacity 0.3s ease;
          }
          
          /* Category hierarchical styles */
          .category-container {
            position: relative;
          }
          
          .subcategory-container {
            position: relative;
            margin-left: 28px;
          }
          
          .subcategory-container::before {
            content: '';
            position: absolute;
            left: -10px;
            top: 0;
            height: 100%;
            width: 1px;
            background-color: #e2e8f0;
          }
          
          .dark .subcategory-container::before {
            background-color: #4b5563;
          }
          
          .subcategory-item {
            position: relative;
          }
          
          .subcategory-item::before {
            display: none;
          }
          
          .dark .subcategory-item::before {
            display: none;
          }
          
          .subcategory-item.active {
            background-color: var(--primary-100, #dbeafe);
          }
          
          .dark .subcategory-item.active {
            background-color: var(--primary-800, #1e3a8a);
          }
          
          @media (max-width: 767px) {
            .sidebar-container {
              position: fixed;
              left: 0;
              top: 0 !important;
              height: 100% !important;
              z-index: 40;
              transform: translateX(-100%);
              width: 250px;
              opacity: 1;
            }
            
            .sidebar-container.open {
              transform: translateX(0);
            }
            
            .content-area.sidebar-open {
              width: 100%;
            }
          }
          
          @media (min-width: 768px) {
            .content-wrapper {
              transition: margin-left 0.3s ease;
            }
            
            .content-wrapper.sidebar-open {
              margin-left: 250px;
            }
          }

          /* Sidebar styles */
          .sidebar-container:focus {
            outline: 2px solid var(--primary-400, #60a5fa);
            outline-offset: -2px;
          }
          
          .dark .sidebar-container:focus {
            outline-color: var(--primary-500, #3b82f6);
          }
        `}</style>
      </Helmet>
      
      <div className="max-w-[1160px] mx-auto pb-2 header-container">
        {/* Header with back button */}
        <section className="flex items-center py-2" role="none">
          <Link
            className="flex flex-row items-center"
            to={`/venuedetails/${venueSlug}`}
            role="button"
            onClick={() => {
              AnalyticsService.trackGAEventHandler("exitedPhotoGallery");
              AnalyticsService.trackMx("exitedPhotoGallery", {
                VenueId: data?.details?.id,
                VenueBrandId: data?.details?.brandId,
                VenueBrandName: data?.details?.brandName,
                VenueAddress: data?.details?.address,
                VenueName: data?.details?.name,
                VenueTypes: data?.details?.types,
              });
            }}
          >
            <ArrowLeft className="mr-1" aria-hidden="true" />
            <span
              className="text-primary-800 dark:text-primary-100 text-sm font-medium"
              tabIndex={0}
            >
              {`${i18n.get("backToVenueDetails")}`}
            </span>
          </Link>
        </section>
      </div>
      
      {/* Combined filter bar and photo grid in one white background container */}
      {!isLoading && reviewPhotos.length > 0 && (
        <div className="max-w-[1160px] mx-auto">
          <div className="bg-white dark:bg-primary-900 rounded-lg overflow-hidden">
            {/* Title row */}
            <h1 className="text-2xl font-bold text-primary-800 dark:text-primary-100 p-4 pb-2 border-b">
              Photo Gallery
            </h1>
            
            {/* Counts and filters row */}
            <div className="flex flex-col px-4 py-2">
              {/* Filter tabs and sidebar toggle - single row on desktop, two rows on mobile */}
              <div className="w-full">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full">
                  <div className="flex items-center flex-shrink-0 mb-2 md:mb-0">
                    <button 
                      onClick={toggleSidebar}
                      className="mr-2 p-1 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800"
                      aria-label="Toggle categories sidebar"
                    >
                      {sidebarOpen ? (
                        <ArrowLeftToLine size={18} className="text-primary-800 dark:text-primary-100" />
                      ) : (
                        <Menu size={18} className="text-primary-800 dark:text-primary-100" />
                      )}
                    </button>
                    <span className="text-sm whitespace-nowrap">{totalSubcategories} spaces</span>
                    <span className="px-1 md:px-3 text-primary-400 dark:text-primary-300">•</span>
                    <span className="text-sm whitespace-nowrap mr-2 md:mr-4">{totalPhotos} photos</span>
                  </div>
                  <div className="flex-grow overflow-hidden flex items-center">
                    {renderFilterTabs()}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Main content area with sidebar and photo grid */}
            <div className="main-layout w-full">
              {/* Sidebar - now part of the layout flow */}
              <div 
                ref={sidebarRef}
                className={cn(
                  "sidebar-container bg-white dark:bg-primary-900 overflow-y-auto",
                  sidebarOpen ? "open" : "closed"
                )}
                tabIndex={sidebarOpen ? 0 : -1}
                aria-label="Category navigation sidebar"
                aria-hidden={!sidebarOpen}
                data-hidden={!sidebarOpen ? "true" : "false"}
                style={!sidebarOpen ? { 
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  position: 'absolute',
                  left: '-9999px',
                  opacity: 0
                } : {
                  position: 'static',
                  visibility: 'visible',
                  pointerEvents: 'auto',
                  opacity: 1
                }}
                role="region"
                onKeyDown={(e) => {
                  // Prevent keyboard navigation into the sidebar when it's closed
                  if (!sidebarOpen && e.key === 'Tab') {
                    e.preventDefault();
                  }
                }}
              >
                <div className="pt-2 pr-2">
                  {/* Category List */}
                  <div className="space-y-2">
                    {Object.entries(photosByCategory)
                      // Filter categories based on the active filter
                      .filter(([categoryName]) => activeFilter === "All" || activeFilter === categoryName)
                      .map(([categoryName, subcategories]) => {
                      // Track expanded state for each category
                      const isExpanded = expandedCategories.includes(categoryName);
                      
                      return (
                        <div key={categoryName} className="mb-2 category-container">
                          {/* Category Button - Now only toggles expansion */}
                          <button
                            onClick={() => toggleCategoryExpansion(categoryName)}
                            className={cn(
                              "w-full px-3 py-2 flex items-start justify-between relative",
                              activeFilter === categoryName
                                ? "bg-primary-50 dark:bg-primary-800/40"
                                : "hover:bg-gray-100 dark:hover:bg-primary-800/20"
                            )}
                            tabIndex={sidebarOpen ? 0 : -1}
                            aria-expanded={isExpanded}
                            disabled={!sidebarOpen}
                          >
                            {activeFilter === categoryName && (
                              <div className="absolute left-0 top-0 w-0.5 h-full bg-gray-300 dark:bg-gray-600"></div>
                            )}
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mt-1">
                                {getCategoryIcon(categoryName)}
                              </div>
                              <span className="ml-2 font-medium text-left">{categoryName}</span>
                            </div>
                            {/* Up/down caret */}
                            <div className="flex-shrink-0 mt-1.5">
                              {isExpanded ? (
                                <ChevronUp size={20} className="text-primary-500 dark:text-primary-100" />
                              ) : (
                                <ChevronDown size={20} className="text-primary-500 dark:text-primary-100" />
                              )}
                            </div>
                          </button>
                          
                          {/* Subcategories - Only visible when category is expanded */}
                          {isExpanded && (
                            <div className="subcategory-container">
                              {Object.entries(subcategories).map(([subcategoryName]) => (
                                <div key={subcategoryName} className="relative">
                                  {/* Blue indicator bar for active subcategory */}
                                  {activeFilter === categoryName && activeSubcategory === subcategoryName && (
                                    <div className="absolute left-[-10px] top-0 w-0.5 h-full bg-primary-600"></div>
                                  )}
                                  
                                  <button
                                    onClick={() => handleSubcategorySelect(categoryName, subcategoryName)}
                                    className={cn(
                                      "w-full py-1.5 flex items-center text-sm text-left",
                                      activeFilter === categoryName && activeSubcategory === subcategoryName
                                        ? "bg-primary-100 dark:bg-primary-800/60 text-primary-700 dark:text-primary-100 ml-[-10px] pl-[14px]"
                                        : "pl-1 hover:bg-primary-50 dark:hover:bg-primary-800/20"
                                    )}
                                    tabIndex={sidebarOpen ? 0 : -1}
                                    disabled={!sidebarOpen}
                                  >
                                    <span className="pl-2">{subcategoryName}</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Backdrop for mobile only */}
              {sidebarOpen && (
                <div 
                  className="sidebar-backdrop fixed inset-0 bg-black/30 z-30 md:hidden" 
                  onClick={toggleSidebar}
                />
              )}
              
              {/* Content area - will shrink when sidebar is open */}
              <div className={cn(
                "content-area pb-8",
                sidebarOpen ? "sidebar-open" : ""
              )} role="none">
                {/* Photo grid - scrollable content */}
                {!isLoading && reviewPhotos.length > 0 && renderPhotoGrid()}

                {/* No results */}
                {reviewPhotos?.length === 0 && !isLoading && (
                  <div className="flex flex-col items-center justify-center mt-20">
                    <img
                      src={NoSearchResults}
                      alt="No photos"
                      className="max-w-[70%] mx-auto mb-4"
                      aria-hidden="true"
                    />
                    <p className="text-primary-600 dark:text-primary-300 text-lg">
                      No photos available for this venue
                    </p>
                  </div>
                )}

                {/* Loading state */}
                {isLoading && <Loader />}

                {/* Full image view */}
                {fullImageView && currentImage && (
                  <FullImageViewer
                    images={reviewPhotos.map((photo) => ({
                      src: bucketUrlPrefix + photo.path,
                      alt: photo.alt_text || "",
                      tag_name: photo.tag_name,
                      category_name: photo.category_name,
                      created_at: photo.created_at,
                      photo_type: photo.photo_type,
                      review_photos_id: photo.review_photos_id,
                      width: photo.width,
                      height: photo.height,
                      sort_order: photo.sort_order,
                      qtags: photo.qtags || [],
                      tags: photo.tags || [{
                        tagName: photo.tag_name || 'Uncategorized',
                        categoryDisplaySectionId: ''
                      }],
                      // Use the display sections from the photo
                      displaySections: photo.displaySections,
                      // Add venue details for tracking
                      venueId: data?.details?.id,
                      venueName: data?.details?.name,
                      venueAddress: data?.details?.address,
                      venueBrandId: data?.details?.brandId,
                      venueBrandName: data?.details?.brandName,
                      venueTypes: data?.details?.types
                    }))}
                    currentIndex={reviewPhotos.findIndex(p => p === currentImage)}
                    onClose={() => setFullImageView(false)}
                    onFlag={handleFlagPhoto}
                    showFlag={true}
                    showRightTab={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <FlagVenuePhoto
        photoReviewId={selectedImage?.review_photos_id}
        venueName={data?.details?.name}
        isOpen={isFlagVenuePhotoOpen}
        onClose={() => handleFlagVenuePhotoClose()}
        venueDetails={{
          data: {
            id: data?.details?.id,
            brandId: data?.details?.brandId,
            brandName: data?.details?.brandName,
            address: data?.details?.address,
            name: data?.details?.name,
            types: data?.details?.types,
            business_photos: data?.details?.businessPhotos
          }
        }}
      />
    </div>
  );
}
